import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useT } from "../lib/i18n";
import { openBooking } from "../lib/booking";

const ADVISOR_IMG = "/matteo-casati.jpg";

export default function About() {
    const { t, lang } = useT();
    const navigate = useNavigate();

    useEffect(() => {
        document.title = lang === "it" ? "Lo studio, Casati Consulting" : "About, Casati Consulting";
    }, [lang]);

    return (
        <div className="bg-[#F9F9F7] text-[#1C1C1C]">
            <section className="pt-20 md:pt-28 pb-12">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <p className="eyebrow">{t.about.eyebrow}</p>
                    <h1
                        className="font-display mt-6 text-5xl sm:text-7xl lg:text-[100px] tracking-tight font-medium leading-[0.95]"
                        style={{ letterSpacing: "-0.035em" }}
                        data-testid="about-title"
                    >
                        {t.about.titleA}
                        <br />
                        <span className="italic font-light">{t.about.titleB}</span>
                        <br />
                        <span className="italic font-light text-[#9B7A58]">{t.about.titleC}</span>
                    </h1>
                </div>
            </section>

            <section className="py-16 md:py-24 border-t hair">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-y-10 md:gap-x-12">
                    <div className="col-span-12 md:col-span-5">
                        <div className="aspect-[4/5] overflow-hidden bg-[#EAEAE6]">
                            <img src={ADVISOR_IMG} alt="Matteo Casati" className="w-full h-full object-cover" data-testid="advisor-portrait" />
                        </div>
                        <div className="mt-6 flex items-baseline justify-between">
                            <div>
                                <p className="font-display text-xl font-medium tracking-tight">Matteo Casati</p>
                                <p className="text-xs text-[#1C1C1C]/60 mt-1">{t.about.advisorRole}</p>
                            </div>
                            <p className="eyebrow">{lang === "it" ? "Zurigo" : "Zürich"}</p>
                        </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:col-start-7">
                        <p className="eyebrow">{t.about.mandateEyebrow}</p>
                        <p
                            className="mt-5 text-2xl md:text-3xl font-display font-medium leading-tight tracking-tight"
                            style={{ letterSpacing: "-0.02em" }}
                        >
                            {t.about.mandate}
                        </p>
                        <div className="mt-10 space-y-6 text-[#1C1C1C]/75 leading-relaxed">
                            <p>{t.about.p1}</p>
                            <p>{t.about.p2}</p>
                            <p>{t.about.p3}</p>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-24 md:py-32 border-t hair bg-[#F4F3EF]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
                    <p className="eyebrow">{t.about.principlesEyebrow}</p>
                    <h2 className="font-display mt-5 text-4xl sm:text-5xl tracking-tight font-medium" style={{ letterSpacing: "-0.03em" }}>
                        {t.about.principlesTitle}
                    </h2>
                    <div className="mt-16 border hair grid grid-cols-1 md:grid-cols-2">
                        {t.about.principles.map(([n, ttl, d]) => (
                            <div
                                key={n}
                                className="p-8 md:p-12 border-t md:border-t-0 md:[&:nth-child(odd)]:border-r hair first:border-t-0 [&:nth-child(2)]:md:border-t-0 [&:nth-child(3)]:border-t [&:nth-child(4)]:border-t"
                                data-testid={`principle-${n}`}
                            >
                                <span className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: "#9B7A58" }}>
                                    {n}
                                </span>
                                <h3 className="font-display mt-8 text-2xl md:text-3xl tracking-tight font-medium" style={{ letterSpacing: "-0.02em" }}>
                                    {ttl}
                                </h3>
                                <p className="mt-4 text-[#1C1C1C]/70 leading-relaxed">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight font-medium max-w-2xl" style={{ letterSpacing: "-0.03em" }}>
                        {t.about.quarterCta}
                    </h2>
                    <button
                        type="button"
                        onClick={() => openBooking(navigate)}
                        className="inline-flex items-center gap-2 px-6 h-12 bg-[#1C1C1C] text-[#F9F9F7] text-[12px] uppercase tracking-[0.22em] font-bold hover:bg-[#9B7A58] transition-colors"
                        data-testid="about-cta-contact"
                    >
                        {t.cta.reach}
                        <ArrowUpRight size={16} strokeWidth={1.5} />
                    </button>
                </div>
            </section>
        </div>
    );
}
